import { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { FaAndroid, FaApple, FaMobileAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';

interface ModalLoginAppProps {
    show: boolean;
    idLocatario: string;
    idEmpresa: string;
    onClose: () => void;
}

const ModalLoginApp = ({ show, idLocatario, idEmpresa, onClose }: ModalLoginAppProps) => {
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [saving, setSaving] = useState(false);
    const [nome, setNome] = useState('');
    const [usuarioId, setUsuarioId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!show || !idLocatario) return;

        const controller = new AbortController();
        const signal = controller.signal;

        const carregarDados = async () => {
            setLoading(true);
            setEmail('');
            setSenha('');
            setUsuarioId(null);
            setNome('');

            let emailCarregado = '';

            try {
                const resp = await api.get(`/UsuarioLocatario/${idLocatario}`, { signal });
                if (resp.data.email) emailCarregado = resp.data.email;
                setUsuarioId(resp.data.id || null);
            } catch (err: any) {
                if (signal.aborted) return;
                if (err?.response?.status !== 404) console.warn('Erro ao buscar UsuarioLocatario:', err);
            }

            try {
                const resp = await api.get(`/Locatario/${idLocatario}`, { signal });
                if (signal.aborted) return;
                setNome(resp.data.nome || '');
                if (!emailCarregado && resp.data.email) emailCarregado = resp.data.email;
            } catch (err: any) {
                if (signal.aborted) return;
                console.warn('Erro ao buscar Locatario:', err);
            }

            if (!signal.aborted) {
                setEmail(emailCarregado);
                setLoading(false);
            }
        };

        carregarDados();

        return () => controller.abort();
    }, [show, idLocatario]);

    const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const senhaValida = senha.length >= 6;
    const formValido = emailValido && senhaValida && !saving && !loading;

    const salvar = async () => {
        setSaving(true);
        try {
            const payload = {
                nome,
                email,
                senha,
                idEmpresa,
                idLocatario,
                ...(usuarioId ? { id: usuarioId } : {}),
            };
            if (usuarioId) {
                await api.put('/UsuarioLocatario/', payload);
            } else {
                const resp = await api.post('/UsuarioLocatario/', payload);
                setUsuarioId(resp.data?.id || null);
            }
            toast.success('Login do locatário salvo com sucesso!');
            onClose();
        } catch (error: any) {
            const msg = error?.response?.data?.mensagem || error?.response?.data?.title || 'Erro ao salvar login do locatário';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <style>{`.modal-login-app-backdrop { z-index: 9999 !important; }`}</style>
            <Modal show={show} onHide={onClose} centered size="lg" style={{ zIndex: 10000 }} backdropClassName="modal-login-app-backdrop">
                <Modal.Header style={{ borderBottom: '1px solid #e2e8f0', padding: '16px 24px' }}>
                    <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1F467E', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaMobileAlt /> Login no App do Locatário
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <Row>
                        <Col md={7}>
                            {nome && (
                                <p style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '16px' }}>
                                    Definir acesso para <strong>{nome}</strong>
                                </p>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="email@exemplo.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    isInvalid={email.length > 0 && !emailValido}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Senha</Form.Label>
                                <Form.Control
                                    type="password"
                                    placeholder="Mínimo 6 caracteres"
                                    value={senha}
                                    onChange={(e) => setSenha(e.target.value)}
                                    autoComplete="new-password"
                                    isInvalid={senha.length > 0 && !senhaValida}
                                />
                                {senha.length > 0 && !senhaValida && (
                                    <Form.Text className="text-danger">A senha deve ter no mínimo 6 caracteres</Form.Text>
                                )}
                            </Form.Group>
                            <Button
                                variant="primary"
                                disabled={!formValido}
                                onClick={salvar}
                                style={{ width: '100%', fontWeight: 600, borderRadius: '8px', padding: '10px', backgroundColor: '#1F467E', borderColor: '#1F467E' }}
                            >
                                {saving ? 'Salvando...' : (usuarioId ? 'Atualizar Login' : 'Criar Login')}
                            </Button>
                            <Button
                                variant="link"
                                onClick={onClose}
                                style={{ width: '100%', color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500, marginTop: '8px', textDecoration: 'none' }}
                            >
                                Agora não
                            </Button>
                        </Col>
                        <Col md={5} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid #e2e8f0', paddingLeft: '24px' }}>
                            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                                <FaMobileAlt style={{ fontSize: '3rem', color: '#1F467E', marginBottom: '8px', display: 'block', margin: '0 auto 8px' }} />
                                <h6 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>App do Locatário</h6>
                                <p style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.4, margin: 0 }}>
                                    Seu locatário acessa cobranças, contratos e vistorias direto do celular.
                                </p>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                <a
                                    href="https://play.google.com/store/apps/details?id=com.locasmartpro.gestaodelocadoras&pcampaignid=web_share"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px',
                                        borderRadius: '8px', border: '1px solid #e2e8f0', textDecoration: 'none',
                                        color: '#1e293b', fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.2s',
                                    }}
                                >
                                    <FaAndroid style={{ fontSize: '1.2rem', color: '#3DDC84' }} />
                                    Baixar no Android
                                </a>
                                <a
                                    href="https://apps.apple.com/br/app/locasmartpro-app-locatário/id6757689416"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px',
                                        borderRadius: '8px', border: '1px solid #e2e8f0', textDecoration: 'none',
                                        color: '#1e293b', fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.2s',
                                    }}
                                >
                                    <FaApple style={{ fontSize: '1.2rem', color: '#000' }} />
                                    Baixar no iOS
                                </a>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ModalLoginApp;
